import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'

const SITE_URL = 'https://mojopayr.com'
const DEFAULT_TITLE = 'MojoPayr - Send, Pay, and Support Home'
const DEFAULT_DESCRIPTION =
    'MojoPayr helps diaspora and local users send money, fund wallets, pay bills, buy tickets, support communities, and track receipts across supported African markets.'
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`

const publicPages = {
    '/': {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        schemaType: 'FinancialService',
    },
    '/help-center': {
        title: 'MojoPayr Help Center - Payments, Wallets, and Receipts',
        description: 'Find help for MojoPayr wallet funding, money transfers, bills, tickets, receipts, merchant payments, and account support.',
        schemaType: 'WebPage',
    },
    '/contact': {
        title: 'Contact MojoPayr Support',
        description: 'Contact MojoPayr for help with payments, wallets, transfers, receipts, business tools, compliance, and account questions.',
        schemaType: 'ContactPage',
    },
    '/faq': {
        title: 'MojoPayr FAQ - Send Money, Pay Bills, and Fund Wallets',
        description: 'Answers to common MojoPayr questions about sending money, wallet funding, fees, receipts, compliance, and supported markets.',
        schemaType: 'FAQPage',
    },
    '/status': {
        title: 'MojoPayr Status - Service Availability',
        description: 'Check MojoPayr service status for wallet, payment, transfer, and merchant services.',
        schemaType: 'WebPage',
    },
    '/about': {
        title: 'About MojoPayr - Cashless Payments for Diaspora and Local Markets',
        description: 'Learn how MojoPayr supports diaspora families, local users, merchants, councils, events, community funds, and digital receipts.',
        schemaType: 'AboutPage',
    },
    '/careers': {
        title: 'Careers at MojoPayr',
        description: 'Explore MojoPayr career opportunities and help build practical cashless payment services for diaspora and African markets.',
        schemaType: 'WebPage',
    },
    '/press': {
        title: 'MojoPayr Press and Media',
        description: 'Official MojoPayr press information, company facts, launch messaging, and media contact details.',
        schemaType: 'WebPage',
    },
    '/blog': {
        title: 'MojoPayr Blog - Diaspora Payments and Cashless Africa',
        description: 'Read MojoPayr insights on diaspora money movement, direct payments, cashless markets, receipts, and African fintech services.',
        schemaType: 'Blog',
    },
    '/legal/privacy': {
        title: 'MojoPayr Privacy Policy',
        description: 'Read how MojoPayr collects, uses, stores, and protects user information across its website, app, and services.',
        schemaType: 'WebPage',
    },
    '/legal/terms': {
        title: 'MojoPayr Terms of Service',
        description: 'Read the rules and responsibilities for using MojoPayr wallet, payment, transfer, and business services.',
        schemaType: 'WebPage',
    },
    '/legal/compliance': {
        title: 'MojoPayr Compliance',
        description: 'Learn about MojoPayr compliance controls, fraud prevention, safe transactions, and operational standards.',
        schemaType: 'WebPage',
    },
    '/legal/kyc': {
        title: 'MojoPayr KYC Policy',
        description: 'Learn how MojoPayr uses Know Your Customer checks to protect users, reduce fraud, and support compliance.',
        schemaType: 'WebPage',
    },
    '/legal/regulatory': {
        title: 'MojoPayr Regulatory Information',
        description: 'Read general regulatory disclosures for MojoPayr services, partners, supported markets, and compliance posture.',
        schemaType: 'WebPage',
    },
    '/payment': {
        title: 'MojoPayr Payment Services - Pay Businesses and Services Back Home',
        description: 'Use MojoPayr to pay businesses, bills, invoices, taxes, tickets, and other local services with clear receipts.',
        schemaType: 'Service',
    },
    '/pay-bills': {
        title: 'Pay Bills with MojoPayr',
        description: 'Pay bills, taxes, school fees, hospital bills, and local services through MojoPayr with trackable digital receipts.',
        schemaType: 'Service',
    },
    '/bank-transfers': {
        title: 'MojoPayr Bank Transfers',
        description: 'Send value to supported bank accounts through MojoPayr with clear fees, exchange rates, and transaction proof.',
        schemaType: 'Service',
    },
}

const blogPosts = {
    '/blog/why-mojopayr-app': {
        title: 'Why MojoPayr Is Built for Real Back-Home Payments',
        description: 'MojoPayr connects diaspora sending, local payments, bills, businesses, and receipts into one practical payment flow.',
    },
    '/blog/no-more-middle-person-payments': {
        title: 'No More Middle Person for Services and Family Support',
        description: 'See how MojoPayr helps users pay services and support family needs more directly with clearer proof.',
    },
}

const privatePatterns = [
    /^\/dashboard/,
    /^\/send/,
    /^\/tickets/,
    /^\/wallet/,
    /^\/transactions/,
    /^\/notifications/,
    /^\/profile/,
    /^\/settings/,
    /^\/donations/,
    /^\/contributions/,
    /^\/invoices/,
    /^\/merchant/,
    /^\/government/,
    /^\/login/,
    /^\/register/,
    /^\/forgot-password/,
    /^\/pay\/invoice/,
    /^\/invoice/,
    /^\/receipt/,
    /^\/contribute/,
    /^\/payment\/amount/,
    /^\/payment\/confirm/,
]

const upsertMeta = (selector, attributes) => {
    let tag = document.head.querySelector(selector)
    if (!tag) {
        tag = document.createElement('meta')
        document.head.appendChild(tag)
    }

    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value))
}

const upsertLink = (rel, href) => {
    let tag = document.head.querySelector(`link[rel="${rel}"]`)
    if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', rel)
        document.head.appendChild(tag)
    }
    tag.setAttribute('href', href)
}

const upsertJsonLd = (id, payload) => {
    let tag = document.getElementById(id)
    if (!tag) {
        tag = document.createElement('script')
        tag.id = id
        tag.type = 'application/ld+json'
        document.head.appendChild(tag)
    }
    tag.textContent = JSON.stringify(payload)
}

const removeJsonLd = (id) => {
    const tag = document.getElementById(id)
    if (tag) tag.remove()
}

const RouteSeo = () => {
    const location = useLocation()
    const params = useParams()

    useEffect(() => {
        const path = location.pathname.replace(/\/$/, '') || '/'
        const isPrivate = privatePatterns.some((pattern) => pattern.test(path))
        const page = blogPosts[path] || publicPages[path] || publicPages['/']
        const canonical = `${SITE_URL}${path === '/' ? '/' : path}`
        const title = page.title || DEFAULT_TITLE
        const description = page.description || DEFAULT_DESCRIPTION
        const robots = isPrivate ? 'noindex,nofollow,noarchive' : 'index,follow'

        document.documentElement.setAttribute('lang', 'en')
        document.title = title
        upsertLink('canonical', canonical)
        upsertMeta('meta[name="description"]', { name: 'description', content: description })
        upsertMeta('meta[name="robots"]', { name: 'robots', content: robots })
        upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'MojoPayr' })
        upsertMeta('meta[property="og:type"]', { property: 'og:type', content: page.schemaType === 'Blog' ? 'blog' : 'website' })
        upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical })
        upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
        upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description })
        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE })
        upsertMeta('meta[property="twitter:card"]', { property: 'twitter:card', content: 'summary_large_image' })
        upsertMeta('meta[property="twitter:url"]', { property: 'twitter:url', content: canonical })
        upsertMeta('meta[property="twitter:title"]', { property: 'twitter:title', content: title })
        upsertMeta('meta[property="twitter:description"]', { property: 'twitter:description', content: description })
        upsertMeta('meta[property="twitter:image"]', { property: 'twitter:image', content: DEFAULT_IMAGE })

        if (!isPrivate) {
            upsertJsonLd('route-seo-jsonld', {
                '@context': 'https://schema.org',
                '@type': page.schemaType || 'WebPage',
                name: title,
                description,
                url: canonical,
                publisher: {
                    '@type': 'Organization',
                    name: 'MojoPayr',
                    url: SITE_URL,
                    logo: `${SITE_URL}/logo.png`,
                },
                ...(params.slug ? { headline: title } : {}),
            })
        } else {
            removeJsonLd('route-seo-jsonld')
        }
    }, [location.pathname, params.slug])

    return null
}

export default RouteSeo
